import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Chip,
    Alert,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    List,
    ListItem,
    ListItemText,
    Divider,
    Avatar
} from '@mui/material';
import {
    Edit,
    ExpandMore,
    Assessment,
    ArrowBack,
    Star
} from '@mui/icons-material';
import { useUser } from '../../contexts/UserContext';
import { useNavigate, useParams } from 'react-router-dom';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { handleFirestoreError } from '../../utils/errorHandling';
import {
    doc,
    getDoc
} from 'firebase/firestore';
import { db } from '../../firebaseClient';
import { EvaluationSchema, EvaluationCategory } from './EvaluationSchemaList';

const EvaluationSchemaDetails: React.FC = () => {
    const { user, isTenantAdmin, isGlobalAdmin } = useUser();
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();

    const [schema, setSchema] = useState<EvaluationSchema | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useDocumentTitle(schema ? `Framework: ${schema.name}` : 'Framework Details');

    useEffect(() => {
        if (id) {
            loadSchema();
        }
    }, [id]);

    const loadSchema = async () => {
        try {
            setLoading(true);
            setError('');

            if (!id) return;

            // Check if this is a global schema route
            const isGlobalSchemaRoute = window.location.pathname.includes('/global/');
            const collectionName = isGlobalSchemaRoute ? 'globalEvaluationSchemas' : 'evaluationSchemas';

            let schemaDoc = await getDoc(doc(db, collectionName, id));

            // If not found in the expected collection, try the other one
            let actualCollection = collectionName;
            if (!schemaDoc.exists()) {
                const alternateCollection = isGlobalSchemaRoute ? 'evaluationSchemas' : 'globalEvaluationSchemas';
                schemaDoc = await getDoc(doc(db, alternateCollection, id));
                actualCollection = alternateCollection;
            }

            if (!schemaDoc.exists()) {
                setError('Evaluation framework not found');
                return;
            }

            const schemaData = schemaDoc.data() as EvaluationSchema;

            // Check permissions for viewing
            if (user && !isGlobalAdmin() && schemaData.tenantId !== user.tenantId && !schemaData.isGlobal) {
                setError('You do not have permission to view this framework');
                return;
            }

            setSchema({ ...(schemaData as any), id, _collection: actualCollection });

        } catch (error) {
            console.error('Error loading framework:', error);
            handleFirestoreError(error, 'Loading evaluation framework', setError);
        } finally {
            setLoading(false);
        }
    };

    const canEditSchema = () => {
        if (!schema || !user) return false;
        return isGlobalAdmin() || (isTenantAdmin() && schema.tenantId === user.tenantId);
    };

    const formatDate = (timestamp: any) => {
        if (!timestamp) return '';
        return new Date(timestamp.seconds * 1000).toLocaleDateString();
    };

    const getCriteriaCount = (categories: EvaluationCategory[]) => {
        if (!categories) return 0;
        return categories.reduce((total, category) => {
            return total + (category.criteria ? category.criteria.length : 0);
        }, 0);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!schema) {
        return (
            <Alert severity="error">
                Evaluation framework not found or you don't have permission to view it.
            </Alert>
        );
    }

    return (
        <Box sx={{ flexGrow: 1 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button
                        startIcon={<ArrowBack />}
                        onClick={() => navigate('/evaluation-schemas')}
                    >
                        Back to Frameworks
                    </Button>
                    <Divider orientation="vertical" flexItem />
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="h4">
                                {schema.name}
                            </Typography>
                            {schema.isDefault && (
                                <Chip
                                    icon={<Star />}
                                    label="Default"
                                    color="primary"
                                    variant="outlined"
                                />
                            )}
                            {schema.isGlobal && (
                                <Chip
                                    label="Global"
                                    color="info"
                                    variant="outlined"
                                />
                            )}
                        </Box>
                        <Typography variant="body1" color="text.secondary">
                            Version {schema.version} • {schema.isActive ? 'Active' : 'Inactive'}
                        </Typography>
                    </Box>
                </Box>
                {canEditSchema() && id && (
                    <Button
                        variant="contained"
                        startIcon={<Edit />}
                        onClick={() => navigate(`/evaluation-schemas/${id}/edit`)}
                    >
                        Edit Framework
                    </Button>
                )}
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Framework Info */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Framework Overview
                    </Typography>

                    {schema.description && (
                        <Typography variant="body1" sx={{ mb: 3 }}>
                            {schema.description}
                        </Typography>
                    )}

                    <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                        <Chip
                            label={`${schema.categories.length} Categories`}
                            variant="outlined"
                        />
                        <Chip
                            label={`${getCriteriaCount(schema.categories)} Total Criteria`}
                            variant="outlined"
                        />
                    </Box>

                    <Typography variant="body2" color="text.secondary">
                        Created: {formatDate(schema.createdAt)} by {schema.createdBy?.displayName || schema.createdBy?.email}
                    </Typography>
                    {schema.updatedAt && (
                        <Typography variant="body2" color="text.secondary">
                            Last updated: {formatDate(schema.updatedAt)} by {schema.updatedBy?.displayName || schema.updatedBy?.email}
                        </Typography>
                    )}
                </CardContent>
            </Card>

            {/* Categories and Criteria */}
            <Typography variant="h6" sx={{ mb: 2 }}>
                Evaluation Categories
            </Typography>

            {schema.categories.map((category, categoryIndex) => (
                <Accordion key={categoryIndex} defaultExpanded={categoryIndex === 0}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                            <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 32, height: 32 }}>
                                <Assessment />
                            </Avatar>
                            <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="h6">
                                    {category.name}
                                </Typography>
                                {category.description && (
                                    <Typography variant="body2" color="text.secondary">
                                        {category.description}
                                    </Typography>
                                )}
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Chip
                                    label={`${category.criteria.length} criteria`}
                                    size="small"
                                    variant="outlined"
                                />
                                <Chip
                                    label={`Weight: ${category.weight}`}
                                    size="small"
                                    variant="outlined"
                                />
                            </Box>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        <List>
                            {category.criteria.map((criteria, criteriaIndex) => (
                                <React.Fragment key={criteriaIndex}>
                                    <ListItem sx={{ px: 0 }}>
                                        <ListItemText
                                            primary={criteria.name}
                                            secondary={
                                                <Box>
                                                    {criteria.description && (
                                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                                            {criteria.description}
                                                        </Typography>
                                                    )}
                                                    {criteria.scoringMethodology && (
                                                        <Box sx={{ mb: 1, p: 1, backgroundColor: 'grey.50', borderRadius: 1 }}>
                                                            <Typography variant="caption" color="primary" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                                                                Scoring Methodology:
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                                                                {criteria.scoringMethodology}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                        <Chip
                                                            label={`Weight: ${criteria.weight}`}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                        {criteria.ratingScale && (
                                                            <Chip
                                                                label={`Scale: ${criteria.ratingScale.min}-${criteria.ratingScale.max}`}
                                                                size="small"
                                                                variant="outlined"
                                                            />
                                                        )}
                                                    </Box>
                                                </Box>
                                            }
                                        />
                                    </ListItem>
                                    {criteriaIndex < category.criteria.length - 1 && <Divider />}
                                </React.Fragment>
                            ))}
                        </List>
                    </AccordionDetails>
                </Accordion>
            ))}
        </Box>
    );
};

export default EvaluationSchemaDetails;
